import { useState } from "react";
import { Lock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import type { AdminMember } from "@/hooks/useAdminMembers";
import { cn } from "@/lib/utils";
import { PersonAvatar } from "./PersonAvatar";
import { FOKUS } from "./planungGestaltung";

interface Props {
  /** Alle Personen, die die Planungszentrale nutzen (Admin-Rolle). */
  mitglieder: AdminMember[];
  ausgeblendet: string[];
  onChange: (ausgeblendet: string[]) => void;
  /** Wer die Aufgabe erstellt hat (kann nicht ausgeblendet werden). */
  erstellerId: string | null;
  ichId: string | null;
  /** Zugewiesene Person; wird sie ausgeblendet, fragt das Feld nach einem Ersatz. */
  zugewiesen: string | null;
  onZuweisen: (userId: string | null) => void;
}

/**
 * „Ausblenden für“: Personen, für die diese Aufgabe nicht existiert. Durchgesetzt wird das
 * in der Datenbank (RESTRICTIVE RLS und Trigger, Migration 20261008140000); dieses Feld
 * bietet nur an, was dort erlaubt ist: nicht die erstellende Person, nicht man selbst.
 */
export function AusblendenFeld({ mitglieder, ausgeblendet, onChange, erstellerId, ichId, zugewiesen, onZuweisen }: Props) {
  const [ersatzFuer, setErsatzFuer] = useState<string | null>(null);
  const waehlbar = mitglieder.filter((m) => m.user_id !== ichId && m.user_id !== erstellerId);
  const name = (id: string) => mitglieder.find((m) => m.user_id === id)?.displayName ?? "Unbekannt";

  const umschalten = (id: string) => {
    if (ausgeblendet.includes(id)) {
      onChange(ausgeblendet.filter((x) => x !== id));
      return;
    }
    if (zugewiesen === id) {
      setErsatzFuer(id);
      return;
    }
    onChange([...ausgeblendet, id]);
  };

  const ersatzWaehlen = (neu: string | null) => {
    if (!ersatzFuer) return;
    onZuweisen(neu);
    onChange([...ausgeblendet, ersatzFuer]);
    setErsatzFuer(null);
  };

  const ersatzKandidaten = ersatzFuer
    ? mitglieder.filter((m) => m.user_id !== ersatzFuer && !ausgeblendet.includes(m.user_id))
    : [];

  return (
    <div className="space-y-2">
      {waehlbar.length === 0 ? (
        <p className="text-[13px] text-[#4E6274]">Niemand außer dir und der erstellenden Person.</p>
      ) : (
        <ul className="grid gap-1 sm:grid-cols-2">
          {waehlbar.map((m) => {
            const aus = ausgeblendet.includes(m.user_id);
            return (
              <li key={m.user_id}>
                <label
                  className={cn(
                    "flex min-h-11 cursor-pointer items-center gap-2 rounded-xl px-2.5 py-1.5 transition-colors",
                    aus ? "bg-[#E6F4F4]" : "hover:bg-white dark:hover:bg-muted",
                  )}
                >
                  <Checkbox
                    checked={aus}
                    onCheckedChange={() => umschalten(m.user_id)}
                    className={cn("data-[state=checked]:border-[#227277] data-[state=checked]:bg-[#227277]", FOKUS)}
                    aria-label={`Für ${m.displayName} ausblenden`}
                  />
                  <PersonAvatar person={m} />
                  <span className="flex-1 truncate text-[14px] text-[#1D2934] dark:text-foreground">{m.displayName}</span>
                  {aus && <Lock className="h-3.5 w-3.5 text-[#227277]" aria-label="ausgeblendet" />}
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-[12px] leading-4 text-[#4E6274] dark:text-muted-foreground">
        Für ausgeblendete Personen gibt es die Aufgabe nicht, auch keinen Hinweis darauf.
        {erstellerId && erstellerId !== ichId && ` ${name(erstellerId)} hat sie erstellt und sieht sie immer.`}
      </p>

      <AlertDialog open={!!ersatzFuer} onOpenChange={(o) => !o && setErsatzFuer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Wer bekommt die Aufgabe stattdessen?</AlertDialogTitle>
            <AlertDialogDescription>
              {ersatzFuer ? name(ersatzFuer) : ""} ist zugewiesen. Wer ausgeblendet ist, kann die Aufgabe nicht haben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-1">
            {ersatzKandidaten.map((m) => (
              <button
                key={m.user_id}
                type="button"
                onClick={() => ersatzWaehlen(m.user_id)}
                className={cn("flex min-h-11 items-center gap-2 rounded-xl px-3 text-left hover:bg-[#E6F4F4]", FOKUS)}
              >
                <PersonAvatar person={m} ich={m.user_id === ichId} />
                <span className="text-[14px]">{m.displayName}{m.user_id === ichId ? " (ich)" : ""}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => ersatzWaehlen(null)}
              className={cn("flex min-h-11 items-center rounded-xl px-3 text-left text-[14px] text-[#4E6274] hover:bg-[#F2F0EA]", FOKUS)}
            >
              Niemand
            </button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
